import React, {useMemo} from "react";
import {Alert, Card, Col, Empty, Row, Space, Statistic, Tag, Tooltip, Typography} from "antd";
import {ApiOutlined, CloudServerOutlined, DatabaseOutlined, RobotOutlined, TeamOutlined, ThunderboltOutlined} from "@ant-design/icons";
import {useSystemHealth} from "./system-health-query";

const {Text} = Typography;

/**
 * Is the system healthy right now (roadmap 0.7).
 *
 * <p>Anything the backend could not measure arrives as null and is shown as «اندازه‌گیری نشده»,
 * never as a zero or a green tick. A dashboard that invents a healthy-looking number is worse than
 * no dashboard, because somebody will trust it.
 */
const SystemHealth = () => {
    const {data, isLoading, isError} = useSystemHealth();
    const h = useMemo(() => data?.data || data || {}, [data]);

    /** null/undefined must read as "not measured", not as 0. */
    const val = (v, suffix = "") => (v === null || v === undefined ? "—" : `${v}${suffix}`);

    const rateTag = (pct) => {
        if (pct === null || pct === undefined) {
            return <Tooltip title="در این بازه چیزی ارسال نشده است"><Tag>اندازه‌گیری نشده</Tag></Tooltip>;
        }
        const color = pct >= 99 ? "success" : pct >= 90 ? "warning" : "error";
        return <Tag color={color}>{pct}٪ موفق</Tag>;
    };

    const latency = (l) =>
        l ? (
            <Space size={4} wrap>
                <Tag>میانه {l.p50Ms}ms</Tag>
                <Tag color={l.p95Ms > 3000 ? "error" : l.p95Ms > 1000 ? "warning" : "default"}>
                    ۹۵٪ زیر {l.p95Ms}ms
                </Tag>
                <Tag>بیشینه {l.maxMs}ms</Tag>
            </Space>
        ) : (
            <Tag>اندازه‌گیری نشده</Tag>
        );

    if (isError) {
        return (
            <div style={{padding: 12}}>
                <Alert type="error" showIcon message="دریافت وضعیت سیستم ناموفق بود"/>
            </div>
        );
    }

    const card = (title, icon, body) => (
        <Card size="small" loading={isLoading} title={<Space>{icon}{title}</Space>} style={{height: "100%"}}>
            {body}
        </Card>
    );

    return (
        <div style={{padding: 12}}>
            <Alert
                type="info"
                showIcon
                style={{marginBottom: 12}}
                message={`شمارنده‌ها مربوط به ${h.windowMinutes ?? 15} دقیقه اخیر هستند`}
                description="این صفحه هر ۱۰ ثانیه به‌روزرسانی می‌شود. هر عددی که قابل اندازه‌گیری نباشد «اندازه‌گیری نشده» نمایش داده می‌شود، نه صفر."
            />

            <Row gutter={[12, 12]}>
                <Col xs={24} md={12} lg={8}>
                    {card("ارتباط زنده", <ThunderboltOutlined/>, (
                        <Row gutter={8}>
                            <Col span={8}><Statistic title="کاربران" value={val(h.realtime?.connectedUsers)}/></Col>
                            <Col span={8}><Statistic title="نشست‌ها" value={val(h.realtime?.sessions)}/></Col>
                            <Col span={8}><Statistic title="اشتراک‌ها" value={val(h.realtime?.subscriptions)}/></Col>
                        </Row>
                    ))}
                </Col>

                <Col xs={24} md={12} lg={8}>
                    {card("صف پشتیبانی", <TeamOutlined/>, (
                        <Row gutter={8}>
                            <Col span={12}>
                                <Statistic
                                    title="در انتظار"
                                    value={val(h.queue?.waiting)}
                                    valueStyle={{color: (h.queue?.waiting ?? 0) > 5 ? "#cf1322" : undefined}}
                                />
                            </Col>
                            <Col span={12}><Statistic title="گفت‌وگوی باز" value={val(h.queue?.liveConversations)}/></Col>
                        </Row>
                    ))}
                </Col>

                <Col xs={24} md={12} lg={8}>
                    {card("پایگاه داده", <DatabaseOutlined/>, (
                        <Row gutter={8}>
                            <Col span={8}><Statistic title="فعال" value={val(h.database?.active)}/></Col>
                            <Col span={8}><Statistic title="آزاد" value={val(h.database?.idle)}/></Col>
                            <Col span={8}>
                                <Statistic
                                    title="در صف"
                                    value={val(h.database?.waitingThreads)}
                                    valueStyle={{color: (h.database?.waitingThreads ?? 0) > 0 ? "#cf1322" : undefined}}
                                />
                            </Col>
                        </Row>
                    ))}
                </Col>

                <Col xs={24} lg={12}>
                    {card("دستیار هوشمند", <RobotOutlined/>, (
                        <Space direction="vertical" size={8} style={{width: "100%"}}>
                            <Space wrap>
                                <Text>{val(h.ai?.calls)} فراخوانی</Text>
                                <Text type="secondary">·</Text>
                                <Text>{val(h.ai?.failures)} ناموفق</Text>
                                {rateTag(h.ai?.successRatePercent)}
                            </Space>
                            {latency(h.ai?.latency)}
                        </Space>
                    ))}
                </Col>

                <Col xs={24} lg={12}>
                    {card("پاسخ‌دهی سرویس", <ApiOutlined/>, (
                        <Space direction="vertical" size={8} style={{width: "100%"}}>
                            <Space wrap>
                                <Text>{val(h.http?.requests)} درخواست</Text>
                                <Text type="secondary">·</Text>
                                <Text>{val(h.http?.serverErrors)} خطای سرور</Text>
                                {h.http?.errorRatePercent != null && (
                                    <Tag color={h.http.errorRatePercent > 1 ? "error" : "success"}>
                                        {h.http.errorRatePercent}٪ خطا
                                    </Tag>
                                )}
                            </Space>
                            {latency(h.http?.latency)}
                        </Space>
                    ))}
                </Col>

                <Col xs={24} lg={12}>
                    {card("تحویل پیام — تلگرام", <CloudServerOutlined/>, (
                        <Space wrap>
                            <Text>{val(h.delivery?.telegram?.sent)} ارسال</Text>
                            <Text type="secondary">·</Text>
                            <Text>{val(h.delivery?.telegram?.failed)} ناموفق</Text>
                            {rateTag(h.delivery?.telegram?.successRatePercent)}
                        </Space>
                    ))}
                </Col>

                <Col xs={24} lg={12}>
                    {card("تحویل پیام — ویجت", <CloudServerOutlined/>, (
                        <Space wrap>
                            <Text>{val(h.delivery?.widget?.pushed)} ارسال</Text>
                            <Text type="secondary">·</Text>
                            <Text>{val(h.delivery?.widget?.failed)} ناموفق</Text>
                            {rateTag(h.delivery?.widget?.successRatePercent)}
                        </Space>
                    ))}
                </Col>
            </Row>

            {h.unavailable && Object.keys(h.unavailable).length > 0 && (
                <Card size="small" style={{marginTop: 12}} title="اندازه‌گیری نشده">
                    {Object.entries(h.unavailable).map(([k, v]) => (
                        <div key={k}>
                            <Text code>{k}</Text> <Text type="secondary">— {v}</Text>
                        </div>
                    ))}
                </Card>
            )}

            {!isLoading && !h.at && <Empty description="داده‌ای دریافت نشد"/>}
        </div>
    );
};

export default SystemHealth;
